import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/synapse/Icon';
import { SYSTEM_REVIEW } from '@/lib/clinicalStarterLists';
import type { DispensedLine } from '@/types/api';
import type { ConsultationPayload, PatientSex } from '@/types/patients';
import { readSystem, systemHasContent } from './consult/systemsReview';
import { SummaryCard, SummaryCardRow } from '@/components/synapse/SummaryCard';

interface VisitSummaryPanelProps {
  payload: ConsultationPayload;
  sex: PatientSex;
  /** Medicines dispensed from clinic stock for this visit (from the ledger). */
  dispensed?: DispensedLine[];
  /**
   * True for a visit staff typed up from the doctor's paper. Such a visit has
   * no structured plan, so the plan card — which otherwise always renders, to
   * say "No follow-up" out loud — is left out rather than asserting something
   * nobody recorded either way.
   */
  recordedFromPaper?: boolean;
}

/**
 * Read-only summary of the data captured during a single visit — the fields
 * unique to one consultation (systemic review, diagnoses, plan, the medications
 * recorded that day, and the per-visit obstetric/gynae snapshot). It
 * deliberately omits the longitudinal record (allergies, chronic
 * diseases, past/family/social history, compliance), which is identical across
 * visits and already shown on the patient summary.
 *
 * Visual language mirrors `PatientSummaryPanel`: a stack of `SummaryCard`s,
 * two columns on wide screens. Every card renders only when it has content; if
 * the whole visit is empty, a single empty-state card keeps the page non-blank.
 */
export function VisitSummaryPanel({
  payload,
  sex,
  dispensed = [],
  recordedFromPaper = false,
}: VisitSummaryPanelProps) {
  const { t } = useTranslation();

  const flaggedSystems = SYSTEM_REVIEW.filter((s) => systemHasContent(payload.systemsReview?.[s.key]));
  const systemsCard = flaggedSystems.length > 0 && (
    <SummaryCard titleKey="consult.presenting.systemicReview" icon="info">
      {flaggedSystems.map((sys, i) => {
        const entry = readSystem(payload.systemsReview?.[sys.key]);
        return (
          <SummaryCardRow key={sys.key} first={i === 0}>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-2">
              {t(sys.labelKey)}
            </div>
            {entry.findings.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {entry.findings.map((f, j) => (
                  <Badge key={`${f}-${j}`} tone="neutral" size="md">
                    {f}
                  </Badge>
                ))}
              </div>
            )}
            {entry.note && (
              <p className="mt-1.5 whitespace-pre-wrap text-[12.5px] leading-relaxed text-muted">
                {entry.note}
              </p>
            )}
          </SummaryCardRow>
        );
      })}
    </SummaryCard>
  );

  // One diagnosis card for both ways a diagnosis gets recorded: the free text
  // staff type up from paper, and the picklist chips from the doctor's form.
  const diagnosisNote = payload.diagnosisNote?.trim();
  const diagnosisCard = (diagnosisNote || payload.diagnoses.length > 0) && (
    <SummaryCard titleKey="consult.presenting.diagnosis" icon="info">
      {diagnosisNote && (
        <SummaryCardRow first>
          <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-ink-2">
            {diagnosisNote}
          </p>
        </SummaryCardRow>
      )}
      {payload.diagnoses.length > 0 && (
        <SummaryCardRow first={!diagnosisNote}>
          <div className="flex flex-wrap gap-1.5">
            {payload.diagnoses.map((d, i) => (
              <Badge key={`${d}-${i}`} tone="neutral" size="md">
                {d}
              </Badge>
            ))}
          </div>
        </SummaryCardRow>
      )}
    </SummaryCard>
  );

  // The two medicine sets are separate cards on purpose: one came out of the
  // clinic's stock, the other the patient was sent to buy.
  const dispensedCard = dispensed.length > 0 && (
    <SummaryCard titleKey="patients.visitSummary.cardDispensed" icon="box_out">
      {dispensed.map((line, i) => (
        <SummaryCardRow key={line.movementId} first={i === 0}>
          <div className="flex items-center gap-2.5">
            <span className="text-[13.5px] font-semibold">{line.itemBrandName}</span>
            {(line.itemGenericName || line.itemStrength) && (
              <span className="text-[12.5px] text-muted">
                {[line.itemGenericName, line.itemStrength].filter(Boolean).join(' · ')}
              </span>
            )}
            <span className="ml-auto font-mono text-[13px] font-semibold tabular-nums">
              {line.quantity} {line.itemUnit}
            </span>
          </div>
        </SummaryCardRow>
      ))}
    </SummaryCard>
  );

  const outside = payload.outsideMedicines ?? [];
  const outsideCard = outside.length > 0 && (
    <SummaryCard titleKey="patients.visitSummary.cardOutside" icon="pill">
      {outside.map((m, i) => {
        const instructions = [m.dose, m.frequency, m.duration].filter(Boolean).join(' · ');
        return (
          <SummaryCardRow key={`${m.name}-${i}`} first={i === 0}>
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
              <span className="text-[13.5px] font-semibold">{m.name}</span>
              {instructions && (
                <span className="font-mono text-[12.5px] text-muted">{instructions}</span>
              )}
            </div>
          </SummaryCardRow>
        );
      })}
    </SummaryCard>
  );

  // The plan card always renders: follow-up is shown for every visit — either
  // "Return in N days" or an explicit "No follow-up" pill — so the absence of a
  // follow-up instruction is unambiguous rather than just missing.
  const hasPlan =
    payload.followUpDays != null || payload.investigations.length > 0 || payload.referrals.length > 0;
  const planCard = (!recordedFromPaper || hasPlan) && (
    <SummaryCard titleKey="patients.visitSummary.cardPlan" icon="flask">
      {payload.investigations.length > 0 && (
        <SummaryCardRow first>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-2">
            {t('consult.presenting.investigations')}
          </div>
          <div className="flex flex-col gap-1.5">
            {payload.investigations.map((inv, i) => (
              <div key={`${inv.name}-${i}`} className="flex items-center gap-2">
                <span className="text-[13px] font-medium">{inv.name}</span>
                {inv.withinDays != null && (
                  <span className="ml-auto font-mono text-[12px] text-muted">
                    {t('patients.visitSummary.withinDays', { count: inv.withinDays })}
                  </span>
                )}
              </div>
            ))}
          </div>
        </SummaryCardRow>
      )}
      {payload.referrals.length > 0 && (
        <SummaryCardRow first={payload.investigations.length === 0}>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-2">
            {t('consult.presenting.referrals')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {payload.referrals.map((r, i) => (
              <Badge key={`${r}-${i}`} tone="info" size="md">
                {r}
              </Badge>
            ))}
          </div>
        </SummaryCardRow>
      )}
      <SummaryCardRow first={payload.investigations.length === 0 && payload.referrals.length === 0}>
        {payload.followUpDays != null ? (
          <div className="flex items-center gap-2">
            <Icon name="calendar" size={14} className="text-muted" />
            <span className="text-[13px] font-medium">
              {t('patients.visitSummary.followUp', { count: payload.followUpDays })}
            </span>
          </div>
        ) : (
          <Badge tone="neutral" size="md">
            {t('patients.visitSummary.noFollowUp')}
          </Badge>
        )}
      </SummaryCardRow>
    </SummaryCard>
  );

  const medsCard = payload.currentMedications.length > 0 && (
    <SummaryCard titleKey="patients.visitSummary.cardMeds" icon="pill">
      {payload.currentMedications.map((m, i) => (
        <SummaryCardRow key={`${m.drug.label}-${i}`} first={i === 0}>
          <div className="flex items-center gap-2.5">
            <span className="text-[13.5px] font-semibold">{m.drug.label}</span>
            {(m.dose || m.frequency) && (
              <span className="font-mono text-[12.5px] text-muted">
                {[m.dose, m.frequency].filter(Boolean).join(' · ')}
              </span>
            )}
            <span className="ml-auto">
              <Badge tone="medication" size="sm">
                {t('itemType.medication')}
              </Badge>
            </span>
          </div>
        </SummaryCardRow>
      ))}
    </SummaryCard>
  );

  const obstetricCard = sex === 'female' ? buildObstetricCard(payload, t) : null;

  // A single full-width stack: systems → diagnosis → medicines given → medicines
  // to buy → plan → meds → obstetric, in clinical reading order. Each entry carries a stable key so React can diff the
  // list.
  const cards = ([
    ['systems', systemsCard],
    ['diagnosis', diagnosisCard],
    ['dispensed', dispensedCard],
    ['outside', outsideCard],
    ['plan', planCard],
    ['meds', medsCard],
    ['obstetric', obstetricCard],
  ] as const).filter(([, node]) => node);

  // For a visit from the doctor's form the plan card always renders, so `cards`
  // is never empty. A visit recorded from paper always has a diagnosis or a
  // medicine (the server refuses one with neither), so it is never empty either.
  return (
    <div className="flex flex-col gap-[18px]">
      {cards.map(([key, node]) => (
        <div key={key}>{node}</div>
      ))}
    </div>
  );
}

/**
 * Per-visit obstetric/gynae snapshot card. Renders only when the consultation
 * holds an LMP/EDD/gestation/contraception or free-text gynae/obstetric note —
 * the longitudinal parity & pregnancy counts (patient-level baseline) are NOT
 * shown here.
 */
function buildObstetricCard(p: ConsultationPayload, t: TFunction) {
  const rows: Array<{ label: string; value: string }> = [];
  if (p.gynae?.lmpIso) rows.push({ label: t('patients.obLmp'), value: p.gynae.lmpIso });
  if (p.obstetric?.eddIso) rows.push({ label: t('patients.obEdd'), value: p.obstetric.eddIso });
  if (p.obstetric?.gestationWeeks != null) {
    rows.push({
      label: t('patients.visitSummary.gestation'),
      value: t('patients.visitSummary.gestationWeeks', { count: p.obstetric.gestationWeeks }),
    });
  }
  if (p.gynae?.contraception) {
    rows.push({ label: t('consult.obstetric.contraception'), value: p.gynae.contraception });
  }

  const notes: Array<{ label: string; value: string }> = [];
  if (p.gynae?.cycleNotes?.trim()) {
    notes.push({ label: t('consult.obstetric.cycleNotes'), value: p.gynae.cycleNotes.trim() });
  }
  if (p.gynae?.notes?.trim()) {
    notes.push({ label: t('consult.obstetric.gynaeNotes'), value: p.gynae.notes.trim() });
  }
  if (p.obstetric?.notes?.trim()) {
    notes.push({ label: t('consult.obstetric.obstetric'), value: p.obstetric.notes.trim() });
  }

  if (rows.length === 0 && notes.length === 0) return null;

  return (
    <SummaryCard titleKey="patients.visitSummary.cardObstetric" icon="calendar">
      {rows.length > 0 && (
        <SummaryCardRow first>
          <div className="grid grid-cols-2 gap-2.5">
            {rows.map((r) => (
              <div key={r.label}>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted">
                  {r.label}
                </div>
                <div className="mt-0.5 text-[14px] font-bold tracking-[-0.01em]">{r.value}</div>
              </div>
            ))}
          </div>
        </SummaryCardRow>
      )}
      {notes.map((n, i) => (
        <SummaryCardRow key={n.label} first={rows.length === 0 && i === 0}>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-2">
            {n.label}
          </div>
          <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-ink-2">{n.value}</p>
        </SummaryCardRow>
      ))}
    </SummaryCard>
  );
}
